import config from "../../helper/config";
import APIHandler from "../../handlers/APIHandler";
import DataHandler from "../../handlers/DataHandler";

const QUESTION_META_CACHE_KEY = "assess_section_question_metadata";
const metadataPromises = {};

const toQueryParamValue = (value) => {
  if (Array.isArray(value)) {
    return value.filter(Boolean).join(",");
  }

  return value || "";
};

async function getQuestionMetadata({
  forceRefresh = false,
  includeAssessments = false,} = {}) {

  const cacheKey = `${QUESTION_META_CACHE_KEY}_${includeAssessments ? "with_assessments" : "base"
    }`;

  if (!forceRefresh) {
    const cached = sessionStorage.getItem(cacheKey);

    if (cached) {
      try {
        return JSON.parse(cached);
      } catch (error) {
        sessionStorage.removeItem(cacheKey);
      }
    }
  }

  if (!metadataPromises[cacheKey]) {
    metadataPromises[cacheKey] = fetchQuestionMetadata({
      includeAssessments,
      cacheKey,
    });

    metadataPromises[cacheKey].finally(() => {
      delete metadataPromises[cacheKey];
    });
  }
  return metadataPromises[cacheKey];
}

async function fetchQuestionMetadata({ includeAssessments, cacheKey }) {
  const requestOptions = {
    method: "GET",
    headers: APIHandler.getHeader(config.azure_ad_config.apis.OAT.name),
  };

  const response = await fetch(
    `${config.oat_api_url}/question/new/create-question-meta-data/?include_assessments=${includeAssessments}`,
    requestOptions
  );

  const result = await APIHandler.handleResponse(response);

  sessionStorage.setItem(cacheKey, JSON.stringify(result));

  return result;
}

async function getQuestionLibraryMetadata({ forceRefresh = false } = {}) {
  const cacheKey = "question_library_meta_data";

  if (!forceRefresh) {
    const cached = sessionStorage.getItem(cacheKey);

    if (cached) {
      try {
        return JSON.parse(cached);
      } catch (error) {
        sessionStorage.removeItem(cacheKey);
      }
    }
  }

  if (!metadataPromises[cacheKey]) {
    metadataPromises[cacheKey] = fetchQuestionLibraryMetadata(cacheKey);

    metadataPromises[cacheKey].finally(() => {
      delete metadataPromises[cacheKey];
    });
  }

  return metadataPromises[cacheKey];
}

async function fetchQuestionLibraryMetadata(cacheKey) {
  const requestOptions = {
    method: "GET",
    headers: APIHandler.getHeader(config.azure_ad_config.apis.OAT.name),
  };

  const response = await fetch(
    `${config.oat_api_url}/question/new/library-meta-data/`,
    requestOptions
  );

  const result = await APIHandler.handleResponse(response);

  sessionStorage.setItem(cacheKey, JSON.stringify(result));

  return result;
}

async function getMyQuestionsLibraryPage(
  limit,
  offset,
  sort_column,
  sort_direction,
  search_term,
  category_id,
  selectedCategories,
  selectedAuthors,
  selectedChapters,
  selectedGrades,
  selectedDifficulties,
  selectedLanguages,
  selectedTypes,
  selectedTerms,
  selectedYears,
  selectedsubjects,
  selectedSources,
  selectedTeachers,
  selectedStartDate,
  selectedEndDate
) {
  try {
    const user_id = DataHandler.getFromSession("user_id");

    const requestOptions = {
      method: "GET",
      headers: APIHandler.getHeader(config.azure_ad_config.apis.OAT.name),
    };

    const params = new URLSearchParams({
      limit: limit || 50,
      offset: offset || 0,
      sort_column: sort_column || "created_at",
      sort_direction: sort_direction || "DESC",
      search_term: search_term || "",
      category_id: category_id || "",
      selectedCategories: toQueryParamValue(selectedCategories),
      selectedAuthors: toQueryParamValue(selectedAuthors),
      selectedChapters: toQueryParamValue(selectedChapters),
      selectedGrades: toQueryParamValue(selectedGrades),
      selectedDifficulties: toQueryParamValue(selectedDifficulties),
      selectedLanguages: toQueryParamValue(selectedLanguages),
      selectedTypes: toQueryParamValue(selectedTypes),
      selectedTerms: toQueryParamValue(selectedTerms),
      selectedYears: toQueryParamValue(selectedYears),
      selectedsubjects: toQueryParamValue(selectedsubjects),
      selectedSources: toQueryParamValue(selectedSources),
      selectedTeachers: toQueryParamValue(selectedTeachers),
      selectedStartDate: selectedStartDate || "",
      selectedEndDate: selectedEndDate || "",
    });

    const response = await fetch(
      `${config.oat_api_url}/question/new/library-page/my/${user_id}?${params.toString()}`,
      requestOptions
    );

    return APIHandler.handleResponse(response);
  } catch (error) {
    throw error;
  }
}

async function getAllQuestionsLibraryPage(
  limit,
  offset,
  sort_column,
  sort_direction,
  search_term,
  category_id,
  selectedCategories,
  selectedAuthors,
  selectedChapters,
  selectedGrades,
  selectedDifficulties,
  selectedLanguages,
  selectedTypes,
  selectedTerms,
  selectedYears,
  selectedsubjects,
  selectedSources,
  selectedTeachers,
  selectedStartDate,
  selectedEndDate
) {
  try {
    const requestOptions = {
      headers: APIHandler.getHeader(config.azure_ad_config.apis.OAT.name),
      method: "GET",
    };

    const params = new URLSearchParams({
      limit: limit || 50,
      offset: offset || 0,
      sort_column: sort_column || "created_at",
      sort_direction: sort_direction || "DESC",
      search_term: search_term || "",
      category_id: category_id || "",
      selectedCategories: toQueryParamValue(selectedCategories),
      selectedAuthors: toQueryParamValue(selectedAuthors),
      selectedChapters: toQueryParamValue(selectedChapters),
      selectedGrades: toQueryParamValue(selectedGrades),
      selectedDifficulties: toQueryParamValue(selectedDifficulties),
      selectedLanguages: toQueryParamValue(selectedLanguages),
      selectedTypes: toQueryParamValue(selectedTypes),
      selectedTerms: toQueryParamValue(selectedTerms),
      selectedYears: toQueryParamValue(selectedYears),
      selectedsubjects: toQueryParamValue(selectedsubjects),
      selectedSources: toQueryParamValue(selectedSources),
      selectedTeachers: toQueryParamValue(selectedTeachers),
      selectedStartDate: selectedStartDate || "",
      selectedEndDate: selectedEndDate || "",
    });

    return await fetch(
      `${config.oat_api_url}/question/new/library-page/all?${params.toString()}`,
      requestOptions
    ).then(APIHandler.handleResponse);
  } catch (error) {
    throw error ;
  }
}

async function getQuestionEditDetail(questionId) {
  const requestOptions = {
    method: "GET",
    headers: APIHandler.getHeader(config.azure_ad_config.apis.OAT.name),
  };

  const response = await fetch(
    `${config.oat_api_url}/question/new/edit-detail/${questionId}`,
    requestOptions
  );

  return APIHandler.handleResponse(response);
}

async function checkQuestionAssessmentUsage(question_id) {
    const requestOptions = {
        method: 'GET',
        headers: APIHandler.getHeader(config.azure_ad_config.apis.OAT.name),
    };

    return await fetch(
        `${config.oat_api_url}/question/new/assessment-usage/${question_id}`,
        requestOptions
    ).then(APIHandler.handleResponse);
}

function clearQuestionMetadataCache() {
  sessionStorage.removeItem("assess_section_question_metadata_base");
  sessionStorage.removeItem("assess_section_question_metadata_with_assessments");
  sessionStorage.removeItem("question_library_meta_data");
}

export const questionEditorService = {
  getQuestionMetadata,
  getQuestionLibraryMetadata,
  getMyQuestionsLibraryPage,
  getAllQuestionsLibraryPage,
  getQuestionEditDetail,
  checkQuestionAssessmentUsage,
  clearQuestionMetadataCache,
};